import React, { useEffect, useState } from 'react';
import {IconButton, Flex, Icon} from '@nula/core-ui';

type Platform = 'macos' | 'windows' | 'linux' | null;

async function getWindow() {
  try {
    const { getCurrentWindow } = await import('@tauri-apps/api/window');
    return getCurrentWindow();
  } catch {
    return null;
  }
}

async function detectPlatform(): Promise<Platform> {
  try {
    const { platform } = await import('@tauri-apps/plugin-os');
    const p = await platform();
    if (p === 'macos') return 'macos';
    if (p === 'windows') return 'windows';
    return 'linux';
  } catch {
    return null;
  }
}

// ── macOS — traffic lights (close · minimize · maximize) ──────────────────────

const trafficLightStyle = (color: string): React.CSSProperties => ({
  width: 'var(--icon-sm)',
  height: 'var(--icon-sm)',
  borderRadius: '50%',
  border: 'none',
  background: color,
  cursor: 'pointer',
  padding: 0,
  flexShrink: 0,
});

function MacOSControls({ onClose, onMinimize, onMaximize }: ControlHandlers) {
  return (
    <Flex align="center" gap="xs">
      <button style={trafficLightStyle('#FF5F57')} onClick={onClose} aria-label="Close" />
      <button style={trafficLightStyle('#FEBC2E')} onClick={onMinimize} aria-label="Minimize" />
      <button style={trafficLightStyle('#28C840')} onClick={onMaximize} aria-label="Maximize" />
    </Flex>
  );
}

// ── Windows / Linux — IconButtons (– □ ×) ────────────────────────────────────

function WinControls({ onClose, onMinimize, onMaximize }: ControlHandlers) {
  return (
    <Flex align="center" gap="xs">
      <IconButton size="md" onClick={onMinimize} aria-label="Minimize">
        <Icon name="remove" size={16} aria-hidden="true" />
      </IconButton>
      <IconButton size="md" onClick={onMaximize} aria-label="Maximize">
        <Icon name="crop_square" size={16} aria-hidden="true" />
      </IconButton>
      <IconButton
        size="md"
        hoverBackground="color-mix(in srgb, var(--error) 15%, transparent)"
        onClick={onClose}
        aria-label="Close"
      >
        <Icon name="close" size={16} aria-hidden="true" />
      </IconButton>
    </Flex>
  );
}

// ── Public component ───────────────────────────────────────────────────────────

interface ControlHandlers {
  onClose: () => void;
  onMinimize: () => void;
  onMaximize: () => void;
}

export function WindowControls() {
  const [platform, setPlatform] = useState<Platform>(null);

  useEffect(() => {
    detectPlatform().then(setPlatform);
  }, []);

  const handleMinimize = async () => {
    const win = await getWindow();
    await win?.minimize();
  };
  const handleMaximize = async () => {
    const win = await getWindow();
    await win?.toggleMaximize();
  };
  const handleClose = async () => {
    const win = await getWindow();
    await win?.close();
  };

  if (platform === 'macos') {
    return <MacOSControls onClose={handleClose} onMinimize={handleMinimize} onMaximize={handleMaximize} />;
  }

  return <WinControls onClose={handleClose} onMinimize={handleMinimize} onMaximize={handleMaximize} />;
}
